/**
 * UM documento de apoio: o tipo, a leitura proposta, os campos e a confirmação (E98.F5.T07).
 *
 * Três regras da tela (ADR-0078, ADR-0083 §2.6):
 *
 * - **Ler não grava.** "Ler o documento" pede a proposta ao servidor e preenche o formulário;
 *   só "Confirmar leitura" grava, com o que estiver nos campos depois da conferência.
 * - **Falhar não fecha o caminho.** Leitura indisponível deixa o formulário para digitar.
 * - **Controle travado durante a gravação.** Com o `PUT` em voo, tipo, leitura e campos
 *   ficam desabilitados: trocar o tipo no meio mandaria uma forma e mostraria outra.
 */
import { useState } from 'react';

import { Button, Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  TIPOS_DE_APOIO,
  confirmarLeitura,
  DocumentoDeApoioError,
  eDoContrato,
  leituraDosValores,
  proporLeitura,
  valoresDaLeitura,
  type DocumentoDeApoioData,
  type OrigemDaLeitura,
  type TipoLido,
  type ValoresDaLeitura,
} from '@/lib/documentos_de_apoio';

import { CamposDaLeituraDeApoio } from './CamposDaLeituraDeApoio';
import { ExclusaoDaAnalise } from './ExclusaoDaAnalise';

const t = ptBR.documentosDeApoio;
const rotulos = ptBR.exameEletronico.tabelaDePecas.tipoDaPeca;

const MENSAGEM_DA_FALHA: Record<DocumentoDeApoioError['falha'], string> = {
  concluido: t.concluido,
  forma: t.erroForma,
  nao_e_de_apoio: t.erroNaoEDeApoio,
  indisponivel: t.erroConfirmar,
  foto_do_caso: t.exclusao.erroFotoDoCaso,
  nao_e_imagem: t.exclusao.erroNaoEImagem,
};

function dataDaConfirmacao(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

interface EstadoDoCartao {
  tipo: TipoLido | null;
  valores: ValoresDaLeitura;
  origem: OrigemDaLeitura;
  sugerido: TipoLido | null;
}

function estadoInicial(documento: DocumentoDeApoioData): EstadoDoCartao {
  const tipo = documento.tipo ?? null;
  const confirmada = documento.leitura_confirmada ?? null;
  return {
    tipo,
    valores: tipo === null ? {} : valoresDaLeitura(tipo, confirmada?.leitura),
    origem: confirmada?.origem ?? 'digitada',
    sugerido: null,
  };
}

function CabecalhoDoCartao({
  caseId,
  documento,
  idPrefix,
}: {
  caseId: string;
  documento: DocumentoDeApoioData;
  idPrefix: string;
}): JSX.Element {
  const confirmada = documento.leitura_confirmada ?? null;
  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-sc-2">
        <p className="text-body-md text-fg">{documento.nome}</p>
        <a
          href={`/arquivos/${caseId}/${documento.documento_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-body-sm text-accent-700 underline"
          data-testid={`${idPrefix}-arquivo`}
        >
          {t.abrirArquivo}
        </a>
      </div>
      {confirmada !== null ? (
        <p className="text-caption text-fg-dim" data-testid={`${idPrefix}-confirmada`}>
          {t.confirmadaEm.replace('{data}', dataDaConfirmacao(confirmada.confirmado_em))}
        </p>
      ) : null}
      {documento.concluido ? <p className="text-body-sm text-fg-muted">{t.concluido}</p> : null}
    </>
  );
}

function SeletorDoTipo({
  idPrefix,
  estado,
  travado,
  lendo,
  onTrocar,
  onLer,
}: {
  idPrefix: string;
  estado: EstadoDoCartao;
  travado: boolean;
  lendo: boolean;
  onTrocar: (tipo: TipoLido) => void;
  onLer: () => void;
}): JSX.Element {
  return (
    <>
      <div className="flex flex-wrap items-end gap-sc-3">
        <Field htmlFor={`${idPrefix}-tipo`} label={t.tipo}>
          <Select
            id={`${idPrefix}-tipo`}
            data-testid={`${idPrefix}-tipo`}
            value={estado.tipo ?? ''}
            disabled={travado || eDoContrato(estado.tipo)}
            onChange={(e) => onTrocar(e.target.value as TipoLido)}
          >
            <option value="" disabled>
              {t.tipoEscolher}
            </option>
            {(eDoContrato(estado.tipo) ? [estado.tipo] : TIPOS_DE_APOIO).map((tipo) => (
              <option key={tipo} value={tipo}>
                {rotulos[tipo]}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          type="button"
          variant="secondary"
          disabled={travado}
          onClick={onLer}
          data-testid={`${idPrefix}-ler`}
        >
          {lendo ? t.lendo : t.ler}
        </Button>
      </div>
      {estado.sugerido !== null ? (
        <p className="text-caption text-fg-muted" data-testid={`${idPrefix}-sugerido`}>
          {t.sugerido.replace('{tipo}', rotulos[estado.sugerido])}
        </p>
      ) : null}
    </>
  );
}

function Mensagem({
  idPrefix,
  aviso,
  erro,
}: {
  idPrefix: string;
  aviso: string | null;
  erro: string | null;
}): JSX.Element {
  return (
    <>
      {aviso !== null ? (
        <p role="status" className="text-body-sm text-fg-muted" data-testid={`${idPrefix}-aviso`}>
          {aviso}
        </p>
      ) : null}
      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid={`${idPrefix}-erro`}>
          {erro}
        </p>
      ) : null}
    </>
  );
}

export interface DocumentoDeApoioCartaoProps {
  caseId: string;
  documento: DocumentoDeApoioData;
  /** O caso tem documento concluído: nada muda (o servidor recusaria de qualquer jeito). */
  concluido: boolean;
  onConfirmado: () => void;
}

export function DocumentoDeApoioCartao({
  caseId,
  documento,
  concluido,
  onConfirmado,
}: DocumentoDeApoioCartaoProps): JSX.Element {
  const client = useApiClient();
  const [estado, setEstado] = useState<EstadoDoCartao>(() => estadoInicial(documento));
  const [lendo, setLendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const travado = concluido || documento.concluido || lendo || gravando;
  const idPrefix = `apoio-${documento.documento_id}`;

  const ler = (): void => {
    void (async () => {
      setLendo(true);
      setErro(null);
      setAviso(null);
      try {
        const proposta = await proporLeitura(client, caseId, documento.documento_id, estado.tipo);
        const tipo = estado.tipo ?? proposta.tipo_sugerido ?? null;
        setEstado({
          tipo,
          valores: tipo === null ? {} : valoresDaLeitura(tipo, proposta.leitura),
          origem: proposta.origem,
          sugerido: proposta.tipo_sugerido ?? null,
        });
        setAviso(t.origem[proposta.origem]);
      } catch {
        setErro(t.erroLer);
      } finally {
        setLendo(false);
      }
    })();
  };

  const confirmar = (tipo: TipoLido): void => {
    void (async () => {
      setGravando(true);
      setErro(null);
      try {
        const leitura = leituraDosValores(tipo, estado.valores);
        await confirmarLeitura(client, caseId, documento.documento_id, {
          leitura,
          origem: estado.origem,
        });
        setAviso(t.confirmadaAgora);
        onConfirmado();
      } catch (err) {
        setErro(
          err instanceof DocumentoDeApoioError ? MENSAGEM_DA_FALHA[err.falha] : t.erroConfirmar,
        );
      } finally {
        setGravando(false);
      }
    })();
  };

  const tipo = estado.tipo;
  // E111.F1 (S-08): a imagem do material pode sair da análise; excluída, não pede leitura.
  const imagem = documento.mime_type.startsWith('image/');
  const excluida = Boolean(documento.exclusao_da_analise);
  return (
    <li className="space-y-sc-3 border border-border p-sc-4" data-testid={idPrefix}>
      <CabecalhoDoCartao caseId={caseId} documento={documento} idPrefix={idPrefix} />
      {imagem ? (
        <ExclusaoDaAnalise
          caseId={caseId}
          documentoId={documento.documento_id}
          exclusao={documento.exclusao_da_analise}
          bloqueado={travado}
          onAlterado={() => onConfirmado()}
        />
      ) : null}
      {excluida ? null : (
        <>
          <SeletorDoTipo
            idPrefix={idPrefix}
            estado={estado}
            travado={travado}
            lendo={lendo}
            onTrocar={(novo) =>
              setEstado((atual) => ({
                ...atual,
                tipo: novo,
                valores: valoresDaLeitura(novo, null),
              }))
            }
            onLer={ler}
          />
          {tipo === null ? null : (
            <>
              <CamposDaLeituraDeApoio
                idPrefix={idPrefix}
                tipo={tipo}
                valores={estado.valores}
                disabled={travado}
                onChange={(chave, valor) =>
                  setEstado((atual) => ({
                    ...atual,
                    valores: { ...atual.valores, [chave]: valor },
                  }))
                }
              />
              <Button
                type="button"
                disabled={travado}
                onClick={() => confirmar(tipo)}
                data-testid={`${idPrefix}-confirmar`}
              >
                {gravando ? t.confirmando : t.confirmar}
              </Button>
            </>
          )}
        </>
      )}
      <Mensagem idPrefix={idPrefix} aviso={aviso} erro={erro} />
    </li>
  );
}
